<template>
  <section class="wt-timeline">
    <div class="wt-timeline-inner">
      <welcome-title-advanced :title-text="timelineTitle" />
      <div
        class="wt-timeline-grid"
        :class="`wt-timeline-grid--${countEvents}`"
      >
        <div
          class="wt-timeline-progress"
          :class="`wt-timeline-progress--${countEvents}`"
        />
        <div
          v-for="(event, i) in timelineEvents"
          :key="i"
          class="wt-timeline-content"
          :class="{
            'is-selected-timeframe':
              event === currentTimelineEvent ||
              (currentDateIsBeyondTimelineEvents && i === countEvents - 1),
            'is-current': isTimelineEventToday(event),
            'is-last': currentDateIsBeyondTimelineEvents,
          }"
        >
          <marker-icon class="icon" />
          <h3 class="wt-timeline-content-title">
            {{ event.name }}
          </h3>
          <p class="wt-timeline-content-date">
            {{ event.date }}
          </p>
          <p class="wt-timeline-content-desc">
            {{ event.description }}
          </p>
          <button
            v-show="event.canBeSubscribedTo"
            type="button"
            class="btn btn-outline"
            @click.prevent="showModal(event)"
          >
            {{ i18n.timeline.btnsubscribe }}
          </button>
        </div>
      </div>
      <modal
        v-show="isModalVisible"
        :modal="isModalVisible"
        @close="closeModal"
      >
        <template slot="body">
          <div>
            <h4 class="modal-content-title">
              {{ modalContent ? modalContent.name : '' }}
            </h4>
            <p class="modal-content-date">
              {{ modalContent ? modalContent.date : '' }}
            </p>
            <p class="modal-content-desc">
              {{ modalContent ? modalContent.description : '' }}
            </p>
            <FormulateForm
              name="event-subscriber"
              class="modal-form"
              novalidate
              @submit="handleSubmit($event)"
            >
              <FormulateInput
                v-model="formFields.email"
                class="form-field"
                type="email"
                name="email"
                :aria-label="i18n.modal.emailarialabel"
                :placeholder="i18n.modal.emailplaceholder"
                validation="bail|required|email"
              />
              <FormulateInput
                v-if="sendEventRegistrationsToOrganisation"
                v-model="formFields.location"
                class="form-field"
                type="select"
                :options="timelineLocations"
                validation="bail|required"
              />
              <div class="btn-container">
                <button
                  type="button"
                  class="btn btn-outline"
                  @click.prevent="closeModal"
                >
                  {{ i18n.modal.btnBackLabel }}
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="registrationState.isSending"
                >
                  {{ i18n.modal.btnSendLabel }}
                </button>
              </div>

              <div
                v-if="registrationState.hasSucceeded"
                class="registration-message registration-message--success"
              >
                <div>{{ i18n.modal.successMessage }}</div>
              </div>
              <div
                v-if="registrationState.hasFailed"
                class="registration-message registration-message--error"
              >
                <div>{{ i18n.modal.errorMessage }}</div>
              </div>
            </FormulateForm>
          </div>
        </template>
      </modal>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'WelcomeTimeline',
  components: {
    Modal: () => import(/* webpackChunkName: "main" */ '@/components/modal/Modal.vue'),
    WelcomeTitleAdvanced: () =>
      import(/* webpackChunkName: "main" */ '@/components/welcome/WelcomeTitleAdvanced.vue'),
    MarkerIcon: () => import(/* webpackChunkName: "main"*/ '@/components/svg/Marker.vue'),
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    timelineTitle: {
      type: String,
      default: '',
    },
    pageId: {
      type: String,
      default: () => '',
    },
    componentKey: {
      type: String,
      default: () => '',
    },
    sendEventRegistrationsToOrganisation: {
      type: Boolean,
      default: () => false,
    },
    timelineEvents: {
      type: Array,
      default: () => [],
    },
    timelineLocations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentDate: '03-08-2021',
      isModalVisible: false,
      modalContent: null,
      formFields: {
        email: null,
        location: null,
      },
      registrationState: this.createInitialRegistrationState(),
    };
  },
  computed: {
    countEvents() {
      return this.timelineEvents.length;
    },
    currentTimelineEvent() {
      const currentDate = moment();

      for (const timelineEvent of this.timelineEvents) {
        const eventDate = moment(timelineEvent.date, 'DD-MM-YYYY');

        if (currentDate.isSameOrBefore(eventDate, 'day')) {
          return timelineEvent;
        }
      }

      return null;
    },
    currentDateIsBeyondTimelineEvents() {
      return !this.currentTimelineEvent;
    },
  },
  methods: {
    createInitialRegistrationState() {
      return {
        isSending: false,
        hasSucceeded: false,
        hasFailed: false,
      };
    },
    isTimelineEventToday(timelineEvent) {
      const currentDate = moment();
      const eventDate = moment(timelineEvent.date, 'DD-MM-YYYY');
      return currentDate.isSame(eventDate, 'day');
    },
    showModal(event) {
      this.isModalVisible = true;
      this.modalContent = event;
      this.resetForm();
      document.body.classList.add('is-locked');
    },
    closeModal() {
      this.isModalVisible = false;
      this.modalContent = {};
      document.body.classList.remove('is-locked');
    },
    resetForm() {
      this.registrationState = this.createInitialRegistrationState();
      this.$formulate.reset('event-subscriber');
      this.formFields.email = null;
      this.formFields.location =
        this.timelineLocations && this.timelineLocations[0]
          ? this.timelineLocations[0].value
          : null;
    },
    handleSubmit() {
      this.registrationState = this.createInitialRegistrationState();
      this.registrationState.isSending = true;

      this.sendRegistration()
        .then(() => {
          this.registrationState.hasSucceeded = true;
        })
        .catch(() => {
          this.registrationState.hasFailed = true;
        })
        .finally(() => {
          this.registrationState.isSending = false;
        });
    },
    sendRegistration() {
      const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        }
      };

      const requestBody = {
        pageId: this.pageId,
        componentKey: this.componentKey,
        organisationLocationKey: this.sendEventRegistrationsToOrganisation
          ? this.formFields.location
          : null,
        eventKey: this.modalContent.key,
        registrantEmail: this.formFields.email,
      };

      return axios.post('/umbraco/api/events/register', requestBody, config);
    },
  },
};
</script>

<style lang="postcss">
.wt-timeline {
  @apply w-full bg-white pt-14 pb-16 relative px-5;

  .wt-title-advanced {
    @apply mb-8;
  }

  &-inner {
    @apply container mx-auto relative;
    z-index: 1;
  }

  &-grid {
    @apply grid relative;
    grid-template-columns: min-content auto;

    @screen md {
      grid-template-columns: 1fr;
    }

    &--2 {
      grid-template-rows: 1fr 1fr;

      @screen md {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr;
      }
    }

    &--3 {
      grid-template-rows: 1fr 1fr 1fr;

      @screen md {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr 1fr;
      }
    }

    &--4 {
      grid-template-rows: 1fr 1fr 1fr 1fr;

      @screen md {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr 1fr 1fr;
      }
    }

    &--5 {
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr;

      @screen md {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      }
    }

    &--6 {
      grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;

      @screen md {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
      }
    }
  }

  &-progress {
    @apply bg-primary-delta;
    border-radius: 8px;
    grid-row-start: 1;
    grid-row-end: 2;

    grid-column-start: 1;
    grid-column-end: 2;
    min-width: 10px;
    margin-left: 10px;
    margin-right: 20px;

    @screen md {
      margin-top: 25px;
      margin-bottom: 25px;
      min-height: 5px;
      min-width: auto;
      margin-left: 0;
      margin-right: 0;
    }

    &--2 {
      grid-row-end: 3;

      @screen md {
        grid-row-end: 0;
        grid-column-end: 3;
      }
    }

    &--3 {
      grid-row-end: 4;

      @screen md {
        grid-row-end: 0;
        grid-column-end: 4;
      }
    }

    &--4 {
      grid-row-end: 5;

      @screen md {
        grid-row-end: 0;
        grid-column-end: 5;
      }
    }

    &--5 {
      grid-row-end: 6;

      @screen md {
        grid-row-end: 0;
        grid-column-end: 6;
      }
    }

    &--6 {
      grid-row-end: 7;

      @screen md {
        grid-row-end: 0;
        grid-column-end: 7;
      }
    }
  }

  &-content {
    @apply flex flex-col relative mb-8;
    transform: translateX(-38px);
    padding-left: 48px;

    @screen md {
      @apply items-center mb-0;
      transform: translateY(-48px) translateX(0);
      padding-top: 48px;
      padding-left: 0;
    }

    &-title {
      @apply text-secondary-alpha mb-2;

      @screen md {
        @apply text-lg px-4 text-center;
        @apply flex flex-col justify-end;
        height: 84px;
      }
    }

    &-date {
      @apply mb-2;

      @screen md {
        @apply text-lg px-4;
      }
    }

    &-desc {
      @apply text-left mb-4 whitespace-pre-line;

      @screen md {
        @apply text-lg text-center px-4;
      }
    }

    .icon {
      @apply absolute top-0 hidden;
      width: 26px;
      height: 36px;
      transform: rotate(-90deg);

      @screen md {
        transform: rotate(0);
      }
    }

    &.is-selected-timeframe {
      .icon {
        @apply block;
        left: 0;
        top: -24px;

        @screen md {
          top: 0;
        }
      }

      &.is-current {
        .icon {
          top: 50%;

          @screen md {
            left: auto;
            top: 0;
          }
        }
      }

      &.is-last {
        .icon {
          top: 100%;

          @screen md {
            right: 0;
            left: auto;
            top: 0;
          }
        }
      }
    }
  }

  .modal {
    @apply p-8 rounded-md;

    @screen md {
      max-width: 425px;
    }

    &-body {
      @apply p-0;
    }

    &-header {
      @apply static border-0;
    }

    &-content-title {
      @apply text-secondary-alpha text-center mb-4;
    }

    &-content-date {
      @apply text-center mb-4;
    }

    &-content-desc {
      @apply text-center mb-4 whitespace-pre-line;
    }

    &-form {
      .btn-container {
        @apply flex justify-end;

        .btn {
          min-width: auto;
        }

        .btn-outline {
          @apply mr-4;
        }
      }

      .registration-message {
        @apply mt-5 text-center;

        div {
          @apply whitespace-pre;
        }

        &--success {
          @apply text-success;
        }

        &--error {
          @apply text-danger;
        }
      }
    }
  }
}
</style>
